import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  GraduationCap, Gamepad2, Star, Trophy, CalendarCheck, Receipt, MessageSquare, Brain,
} from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

interface Props {
  childExams: any[];
  childGames: any[];
  todayAttendance: any[];
  selectedChild: string | null;
  overdueInvoices: any[];
  latestMessage: any | null;
  aiSuggestion: string | null;
}

export default function ParentOverview({
  childExams,
  childGames,
  todayAttendance,
  selectedChild,
  overdueInvoices,
  latestMessage,
  aiSuggestion,
}: Props) {
  const recentExams = childExams.slice(0, 3);
  const recentGames = childGames.slice(0, 3);
  const attended = selectedChild ? todayAttendance.some((a) => a.attendee_id === selectedChild) : false;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Recent exams */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-primary" />
            آخر الامتحانات
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {recentExams.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">لا توجد نتائج بعد</p>
          ) : (
            recentExams.map((e) => (
              <div key={e.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Trophy className="w-3.5 h-3.5 text-primary" />
                  <span className="text-foreground truncate max-w-[150px]">{e.exams?.title || "امتحان"}</span>
                </div>
                <Badge variant={
                  (e.percentage || 0) >= 70 ? "default" : (e.percentage || 0) >= 50 ? "secondary" : "destructive"
                } className="text-xs">
                  {e.percentage != null ? `${Math.round(e.percentage)}%` : "-"}
                </Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Recent games */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Gamepad2 className="w-4 h-4 text-primary" />
            آخر الألعاب
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {recentGames.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">لا توجد نتائج بعد</p>
          ) : (
            recentGames.map((g) => (
              <div key={g.id} className="flex items-center justify-between text-sm">
                <span className="text-foreground truncate max-w-[150px]">{g.game_type}</span>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: Math.min(g.stars || 0, 3) }).map((_, i) => (
                    <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                  ))}
                  <Badge variant="outline" className="text-xs">{g.score}/{g.max_score}</Badge>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Attendance today */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <CalendarCheck className="w-4 h-4 text-primary" />
            حضور اليوم
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center gap-2 text-sm ${attended ? "text-emerald-600" : "text-muted-foreground"}`}>
            <div className={`w-3 h-3 rounded-full ${attended ? "bg-emerald-500" : "bg-muted-foreground/30"}`} />
            {attended ? "تم تسجيل الحضور ✔️" : "لم يتم تسجيل الحضور"}
          </div>
          {overdueInvoices.length > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-destructive">
              <Receipt className="w-4 h-4" />
              {overdueInvoices.length} فاتورة متأخرة
            </div>
          )}
        </CardContent>
      </Card>

      {/* Latest message & AI */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Brain className="w-4 h-4 text-primary" />
            ملخص سريع
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {latestMessage && (
            <div className="flex items-start gap-2 text-sm">
              <MessageSquare className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">آخر رسالة</p>
                <p className="text-foreground text-xs line-clamp-2">{latestMessage.content}</p>
              </div>
            </div>
          )}
          {aiSuggestion && (
            <div className="bg-primary/5 rounded-lg p-2 text-xs text-foreground">
              <p className="font-medium text-primary mb-1">💡 اقتراح</p>
              <p className="line-clamp-2">{aiSuggestion}</p>
            </div>
          )}
          {!latestMessage && !aiSuggestion && (
            <p className="text-xs text-muted-foreground text-center py-2">لا توجد تحديثات حالياً</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
